import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Tag, User } from 'lucide-react';
import { blogAPI } from '../utils/api';

export const BlogArticle = () => {
  const { slug } = useParams();
  const navigate  = useNavigate();
  const [post, setPost]     = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    blogAPI.getOne(slug)
      .then(res => {
        const p = res.data;
        setPost(p);

        // SEO dynamique par article
        document.title = `${p.title} | Blog Sax&DJ`;

        const setMeta = (attr, name, content) => {
          let el = document.querySelector(`meta[${attr}="${name}"]`);
          if (!el) { el = document.createElement('meta'); el.setAttribute(attr, name); document.head.appendChild(el); }
          el.setAttribute('content', content);
        };
        const setLink = (rel, href) => {
          let el = document.querySelector(`link[rel="${rel}"]`);
          if (!el) { el = document.createElement('link'); el.setAttribute('rel', rel); document.head.appendChild(el); }
          el.setAttribute('href', href);
        };

        const url = `https://sax-dj.fr/blog/${p.slug}`;
        setMeta('name', 'description', p.excerpt);
        setLink('canonical', url);
        setMeta('property', 'og:title',       p.title);
        setMeta('property', 'og:description', p.excerpt);
        setMeta('property', 'og:url',         url);
        if (p.cover_image_url) setMeta('property', 'og:image', p.cover_image_url);

        // JSON-LD BlogPosting
        let script = document.getElementById('jsonld-article');
        if (!script) { script = document.createElement('script'); script.type = 'application/ld+json'; script.id = 'jsonld-article'; document.head.appendChild(script); }
        script.textContent = JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          "headline": p.title,
          "description": p.excerpt,
          "url": url,
          "datePublished": p.created_at,
          "dateModified":  p.updated_at,
          "author": { "@type": "Organization", "name": p.author, "url": "https://sax-dj.fr" },
          "publisher": { "@type": "Organization", "name": "Sax&DJ", "logo": { "@type": "ImageObject", "url": "https://sax-dj.fr/saxDJLOGO.webp" } },
          ...(p.cover_image_url ? { "image": p.cover_image_url } : {}),
          ...(p.tags && p.tags.length ? { "keywords": p.tags.join(', ') } : {})
        });
      })
      .catch(() => navigate('/blog', { replace: true }))
      .finally(() => setLoading(false));

    return () => {
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) canonical.remove();
      const jsonld = document.getElementById('jsonld-article');
      if (jsonld) jsonld.remove();
    };
  }, [slug, navigate]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-10 h-10 border-4 border-blue-900 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!post) return null;

  return (
    <div className="min-h-screen bg-white">

      {/* Cover image */}
      {post.cover_image_url && (
        <div className="w-full h-64 md:h-96 overflow-hidden">
          <img src={post.cover_image_url} alt={post.title} className="w-full h-full object-cover" />
        </div>
      )}

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">

        {/* Retour */}
        <Link to="/blog" className="inline-flex items-center text-blue-900 hover:text-gold transition-colors text-sm font-medium mb-8">
          <ArrowLeft className="mr-1.5 h-4 w-4" /> Retour au blog
        </Link>

        {/* Méta */}
        <div className="flex flex-wrap gap-4 items-center text-sm text-warm-gray mb-6">
          <span className="flex items-center gap-1.5">
            <Calendar className="h-4 w-4" />
            {new Date(post.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
          </span>
          <span className="flex items-center gap-1.5">
            <User className="h-4 w-4" />
            {post.author}
          </span>
          {post.tags && post.tags.map((tag, i) => (
            <span key={i} className="inline-flex items-center gap-1 text-xs font-semibold text-blue-900 bg-blue-50 px-2 py-1 rounded-full">
              <Tag className="h-3 w-3" />{tag}
            </span>
          ))}
        </div>

        {/* Titre */}
        <h1 className="text-3xl md:text-4xl font-bold text-charcoal mb-6 leading-tight">
          {post.title}
        </h1>

        {/* Extrait */}
        <p className="text-lg text-warm-gray leading-relaxed mb-8 border-l-4 border-gold pl-4 italic">
          {post.excerpt}
        </p>

        {/* Contenu HTML */}
        <div
          className="prose prose-lg max-w-none
            prose-headings:text-charcoal prose-headings:font-bold
            prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4
            prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
            prose-p:text-warm-gray prose-p:leading-relaxed prose-p:mb-4
            prose-strong:text-charcoal
            prose-a:text-blue-900 prose-a:font-semibold hover:prose-a:text-gold
            prose-ul:text-warm-gray prose-ol:text-warm-gray
            prose-li:mb-1
            prose-img:rounded-xl prose-img:shadow-md"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />

        {/* Footer article */}
        <div className="mt-12 pt-8 border-t border-blue-100">
          <div className="bg-gradient-to-br from-blue-900 to-blue-700 rounded-2xl p-8 text-center">
            <p className="text-white font-bold text-xl mb-2">Vous avez un projet ?</p>
            <p className="text-white/80 mb-6 text-sm">Mariage, soirée d'entreprise, cocktail... Parlons-en.</p>
            <Link
              to="/contact"
              className="inline-block bg-gold hover:bg-yellow-500 text-blue-900 font-bold px-6 py-3 rounded-lg transition-colors duration-200"
            >
              Demander un devis gratuit
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};
